import React, { useState, useEffect } from 'react';
import {
  Box,
  Modal,
  Button,
  Typography,
  Radio,
  Checkbox,
  IconButton,
  Paper,
  useTheme,
  useMediaQuery,
  Fade,
  Collapse,
  CircularProgress,
  Alert,
  Snackbar
} from '@mui/material';
import {
  ShieldOutlined,
  WarningOutlined,
  CheckCircleOutline,
  CancelOutlined,
  Close as CloseIcon,
  CelebrationOutlined
} from '@mui/icons-material';
import { keyframes } from '@mui/system';
import axiosInstance from 'src/utils/axios'
import Logo from "src/components/logo";
import Iconify from './Iconify';

// Keyframe animations
const pulseGlow = keyframes`
  0%, 100% {
    box-shadow: 0 10px 30px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.03), 0 0 15px rgba(38, 158, 70, 0.3);
  }
  50% {
    box-shadow: 0 10px 30px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.03), 0 0 25px rgba(38, 158, 70, 0.5), 0 0 35px rgba(38, 158, 70, 0.2);
  }
`;

const shimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const successFadeIn = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.95) translateY(10px);
  }
  100% {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
`;

const glowPulse = keyframes`
  0%, 100% {
    text-shadow: 0 0 15px rgba(38, 158, 70, 0.5), 0 0 25px rgba(38, 158, 70, 0.3);
  }
  50% {
    text-shadow: 0 0 25px rgba(38, 158, 70, 0.8), 0 0 35px rgba(38, 158, 70, 0.5), 0 0 45px rgba(38, 158, 70, 0.3);
  }
`;

// Progress Steps Component
const ProgressSteps = ({ currentStep }) => {
    const steps = [
        { number: 1, label: 'Review' },
        { number: 2, label: 'Select' },
        { number: 3, label: 'Confirm' }
    ];

    return (
        <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: 0.5 }}>
                {steps.map((step, index) => (
                    <React.Fragment key={step.number}>
                        <Box
                            sx={{
                                width: 28,
                                height: 28,
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '13px',
                                fontWeight: 600,
                                bgcolor: currentStep >= step.number ? '#269e46' : '#333',
                                color: currentStep >= step.number ? 'white' : '#666',
                                transition: 'all 0.3s',
                            }}
                        >
                            {step.number}
                        </Box>
                        {index < steps.length - 1 && (
                            <Box
                                sx={{
                                    width: 24,
                                    height: 3,
                                    borderRadius: 2,
                                    bgcolor: currentStep >= step.number + 1 ? '#269e46' : '#333',
                                    transition: 'all 0.3s',
                                }}
                            />
                        )}
                    </React.Fragment>
                ))}
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, fontSize: '11px', color: '#888' }}>
                {steps.map((step) => (
                    <Typography
                        key={step.number}
                        variant="caption"
                        sx={{
                            color: currentStep >= step.number ? '#269e46' : '#888',
                            transition: 'color 0.3s',
                            fontSize: '0.7rem',
                        }}
                    >
                        {step.label}
                    </Typography>
                ))}
            </Box>
        </Box>
    );
};

// Fireworks Component
// const FireworksEffect = ({ active }) => {
//     const [fireworks, setFireworks] = useState([]);

//     useEffect(() => {
//         if (!active) {
//             setFireworks([]);
//             return;
//         }

//         const positions = [
//             { x: 50, y: 40 }, { x: 25, y: 50 }, { x: 75, y: 50 },
//             { x: 35, y: 30 }, { x: 65, y: 30 }, { x: 40, y: 60 }, { x: 60, y: 60 }
//         ];

//         const newFireworks = positions.map((pos, idx) => ({
//             id: idx,
//             x: pos.x,
//             y: pos.y,
//             color: idx % 2 === 0 ? '#269e46' : '#ffffff',
//             particles: Array.from({ length: 16 }, (_, i) => ({
//                 id: i,
//                 angle: (i / 16) * 360,
//                 speed: 80 + Math.random() * 60,
//             })),
//             sparks: Array.from({ length: 24 }, (_, i) => ({
//                 id: i,
//                 angle: Math.random() * 360,
//                 speed: 40 + Math.random() * 100,
//             })),
//         }));

//         setFireworks(newFireworks);

//         const timer = setTimeout(() => {
//             setFireworks([]);
//         }, 6000);

//         return () => clearTimeout(timer);
//     }, [active]);

//     if (!active || fireworks.length === 0) return null;

//     return (
//         <Box
//             sx={{
//                 position: 'fixed',
//                 top: 0,
//                 left: 0,
//                 right: 0,
//                 bottom: 0,
//                 pointerEvents: 'none',
//                 zIndex: 10000,
//                 overflow: 'hidden',
//             }}
//         >
//             {fireworks.map((firework) => (
//                 <Box
//                     key={firework.id}
//                     sx={{
//                         position: 'absolute',
//                         left: `${firework.x}%`,
//                         top: `${firework.y}%`,
//                         transform: 'translate(-50%, -50%)',
//                     }}
//                 >
//                     {/* Glow */}
//                     <Box
//                         sx={{
//                             position: 'absolute',
//                             width: 100,
//                             height: 100,
//                             left: -50,
//                             top: -50,
//                             borderRadius: '50%',
//                             background: `radial-gradient(circle, ${firework.color} 0%, transparent 70%)`,
//                             opacity: 0,
//                             animation: 'glowEffect 0.8s ease-out forwards',
//                             '@keyframes glowEffect': {
//                                 '0%': { transform: 'scale(0)', opacity: 0.8 },
//                                 '50%': { transform: 'scale(1)', opacity: 0.4 },
//                                 '100%': { transform: 'scale(1.5)', opacity: 0 },
//                             },
//                         }}
//                     />

//                     {/* Particles */}
//                     {firework.particles.map((particle) => {
//                         const radians = (particle.angle * Math.PI) / 180;
//                         const tx = Math.cos(radians) * particle.speed;
//                         const ty = Math.sin(radians) * particle.speed;

//                         return (
//                             <Box
//                                 key={particle.id}
//                                 sx={{
//                                     position: 'absolute',
//                                     left: -12,
//                                     top: -12,
//                                     animation: 'particleExplode 1.8s ease-out forwards',
//                                     '@keyframes particleExplode': {
//                                         '0%': { transform: 'translate(0, 0) scale(0)', opacity: 1 },
//                                         '20%': { opacity: 1, transform: `translate(${tx}px, ${ty}px) scale(1)` },
//                                         '100%': {
//                                             transform: `translate(${tx * 1.5}px, ${ty * 1.5 + 30}px) scale(0.3)`,
//                                             opacity: 0
//                                         },
//                                     },
//                                 }}
//                             >
//                                 <Typography
//                                     sx={{
//                                         fontSize: '16px',
//                                         fontWeight: 'bold',
//                                         color: firework.color,
//                                         textShadow: `0 0 10px ${firework.color}80`,
//                                     }}
//                                 >
//                                     H
//                                 </Typography>
//                             </Box>
//                         );
//                     })}
//                 </Box>
//             ))}
//         </Box>
//     );
// };

const FireworksEffect = ({ active }) => {
    const [fireworks, setFireworks] = useState([]);

    useEffect(() => {
        if (!active) {
            setFireworks([]);
            return;
        }

        const positions = [
            { x: 50, y: 40 }, { x: 25, y: 50 }, { x: 75, y: 50 },
            { x: 35, y: 30 }, { x: 65, y: 30 }, { x: 40, y: 60 }, { x: 60, y: 60 }
        ];

        const newFireworks = positions.map((pos, idx) => ({
            id: idx,
            x: pos.x,
            y: pos.y,
            color: idx % 2 === 0 ? '#269e46' : '#ffffff',
            particles: Array.from({ length: 16 }, (_, i) => ({
                id: i,
                angle: (i / 16) * 360,
                speed: 80 + Math.random() * 60,
            })),
        }));

        setFireworks(newFireworks);

        const timer = setTimeout(() => {
            setFireworks([]);
        }, 6000);

        return () => clearTimeout(timer);
    }, [active]);

    if (!active || fireworks.length === 0) return null;

    return (
        <Box
            sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                pointerEvents: 'none',
                zIndex: 9999999, // Higher z-index to ensure it's on top
                overflow: 'hidden',
                // Add glow effect keyframes here
                '@keyframes glowEffect': {
                    '0%': { transform: 'scale(0)', opacity: 0.8 },
                    '50%': { transform: 'scale(1)', opacity: 0.4 },
                    '100%': { transform: 'scale(1.5)', opacity: 0 },
                },
                '@keyframes particleExplode': {
                    '0%': { transform: 'translate(0, 0) scale(0)', opacity: 1 },
                    '20%': { opacity: 1, transform: 'translate(var(--tx), var(--ty)) scale(1)' },
                    '100%': {
                        transform: 'translate(calc(var(--tx) * 1.5), calc(var(--ty) * 1.5 + 30px)) scale(0.3)',
                        opacity: 0
                    },
                },
                '@keyframes sparkTrail': {
                    '0%': { transform: 'translate(0, 0) scale(1)', opacity: 1 },
                    '100%': { transform: 'translate(var(--sx), var(--sy)) scale(0)', opacity: 0 },
                },
            }}
        >
            {fireworks.map((firework) => (
                <Box
                    key={firework.id}
                    sx={{
                        position: 'absolute',
                        left: `${firework.x}%`,
                        top: `${firework.y}%`,
                        transform: 'translate(-50%, -50%)',
                    }}
                >
                    {/* Center Glow */}
                    <Box
                        sx={{
                            position: 'absolute',
                            width: 100,
                            height: 100,
                            left: -50,
                            top: -50,
                            borderRadius: '50%',
                            background: `radial-gradient(circle, ${firework.color} 0%, transparent 70%)`,
                            opacity: 0,
                            animation: 'glowEffect 0.8s ease-out forwards',
                        }}
                    />

                    {/* Center Burst */}
                    <Box
                        sx={{
                            position: 'absolute',
                            width: 20,
                            height: 20,
                            left: -10,
                            top: -10,
                            borderRadius: '50%',
                            background: firework.color,
                            boxShadow: `0 0 20px ${firework.color}`,
                            animation: 'glowEffect 0.8s ease-out forwards',
                        }}
                    />

                    {/* Explosion Particles */}
                    {firework.particles.map((particle) => {
                        const radians = (particle.angle * Math.PI) / 180;
                        const tx = Math.cos(radians) * particle.speed;
                        const ty = Math.sin(radians) * particle.speed;
                        
                        // For the particle trail (sparks)
                        const sparkCount = 3;
                        const sparks = Array.from({ length: sparkCount }, (_, i) => ({
                            id: i,
                            delay: i * 0.1,
                            sx: tx * 0.3,
                            sy: ty * 0.3,
                        }));

                        return (
                            <Box key={particle.id}>
                                {/* Main Particle */}
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        left: -6,
                                        top: -6,
                                        animation: 'particleExplode 1.8s ease-out forwards',
                                        '--tx': `${tx}px`,
                                        '--ty': `${ty}px`,
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: '20px',
                                            fontWeight: 'bold',
                                            color: firework.color,
                                            textShadow: `0 0 15px ${firework.color}`,
                                        }}
                                    >
                                        H
                                    </Typography>
                                </Box>

                                {/* Particle Trail (Sparks) */}
                                {sparks.map((spark) => (
                                    <Box
                                        key={spark.id}
                                        sx={{
                                            position: 'absolute',
                                            left: -3,
                                            top: -3,
                                            animation: 'sparkTrail 0.4s ease-out forwards',
                                            animationDelay: `${spark.delay}s`,
                                            '--sx': `${spark.sx}px`,
                                            '--sy': `${spark.sy}px`,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 4,
                                                height: 4,
                                                borderRadius: '50%',
                                                background: firework.color,
                                                boxShadow: `0 0 8px ${firework.color}`,
                                            }}
                                        />
                                    </Box>
                                ))}
                            </Box>
                        );
                    })}

                    {/* Additional Random Sparks */}
                    {Array.from({ length: 12 }).map((_, i) => {
                        const angle = Math.random() * 360;
                        const radians = (angle * Math.PI) / 180;
                        const distance = 30 + Math.random() * 40;
                        const sx = Math.cos(radians) * distance;
                        const sy = Math.sin(radians) * distance;
                        
                        return (
                            <Box
                                key={`spark-${i}`}
                                sx={{
                                    position: 'absolute',
                                    left: -2,
                                    top: -2,
                                    animation: 'sparkTrail 0.6s ease-out forwards',
                                    animationDelay: `${0.1 + Math.random() * 0.3}s`,
                                    '--sx': `${sx}px`,
                                    '--sy': `${sy}px`,
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 3,
                                        height: 3,
                                        borderRadius: '50%',
                                        background: '#ffffff',
                                        boxShadow: '0 0 6px #ffffff',
                                    }}
                                />
                            </Box>
                        );
                    })}
                </Box>
            ))}
        </Box>
    );
};

const useSubscriptionActions = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const checkPreauthStatus = async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await axiosInstance.get('/api/user/dashboard/preauth-status');
            setLoading(false);
            return { success: true, data: response.data };
        } catch (err) {
            setError(err);
            setLoading(false);
            return { success: false, error: err };
        }
    };

    const approveSubscription = async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await axiosInstance.get('/api/user/dashboard/preauth-approve');
            setLoading(false);
            return { success: true, data: response.data };
        } catch (err) {
            setError(err);
            setLoading(false);
            return { success: false, error: err };
        }
    };

    const declineSubscription = async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await axiosInstance.get('/api/user/dashboard/preauth-decline');
            setLoading(false);
            return { success: true, data: response.data };
        } catch (err) {
            setError(err);
            setLoading(false);
            return { success: false, error: err };
        }
    };

    return {
        checkPreauthStatus,
        approveSubscription,
        declineSubscription,
        loading,
        error,
        resetError: () => setError(null),
    };
};


// Main PreAuth Popup Component - Modified to show only after login
const LoginSubscriptionPopup = ({
    userName = "Subscriber",
    // logoUrl,
    deadlineDate = "February 13th",
    onSuccess,
    onCancel,
    showOnLogin = false,
    user = null,
    distributorId = null,
    apiKey = null,
    userEmail = null,
    username = null
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isSmallScreen = useMediaQuery('(max-height: 700px)');
    const {
        checkPreauthStatus,
        approveSubscription,
        declineSubscription,
        loading,
        error,
        resetError
    } = useSubscriptionActions();
    // State
    // State
    const [open, setOpen] = useState(false);
    const [selectedOption, setSelectedOption] = useState(null);
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [showCancellation, setShowCancellation] = useState(false);
    const [showTermsModal, setShowTermsModal] = useState(false);
    const [showBillingModal, setShowBillingModal] = useState(false);
    const [showFireworks, setShowFireworks] = useState(false);
    const [apiResponse, setApiResponse] = useState(null);
    const [showError, setShowError] = useState(false);
    const [checkingStatus, setCheckingStatus] = useState(false);
    const [preauthStatus, setPreauthStatus] = useState(null);
// const [showTermsModal, setShowTermsModal] = useState(false);
// const [showBillingModal, setShowBillingModal] = useState(false);
    const currentStep = React.useMemo(() => {
        if (!selectedOption) return 1;
        if (selectedOption === 'approve' && !termsAccepted) return 2;
        return 3;
    }, [selectedOption, termsAccepted]);

  useEffect(() => {
  const checkAndShowPopup = async () => {
    if (!showOnLogin) return;

    // REMOVE THIS SESSIONSTORAGE CHECK
    // const popupShown = sessionStorage.getItem(popupKey);
    // if (popupShown === 'true') {
    //   return;
    // }

    // Check preauth status from API
    setCheckingStatus(true);
    const result = await checkPreauthStatus();
    setCheckingStatus(false);

    if (result.success) {
      const { preauth_status } = result.data;
      setPreauthStatus(preauth_status);

      // Only show popup if status is "pending"
      if (preauth_status === 'pending') {
        const timer = setTimeout(() => {
          setOpen(true);
          // REMOVE THIS LINE: sessionStorage.setItem(popupKey, 'true');
        }, 2000);

        return () => clearTimeout(timer);
      }
    } else {
      // Show error if API call fails
      setShowError(true);
    }
  };

  checkAndShowPopup();
}, [showOnLogin, user]);

    const handleClose = () => {
        setOpen(false);
    };

    const handleOptionSelect = (option) => {
        setSelectedOption(option);
        if (option === 'decline') {
            setTermsAccepted(false);
        }
    };

    const handleSubmit = async () => {
        if (selectedOption === 'approve' && !termsAccepted) return;

        let apiResult;

        if (selectedOption === 'approve') {
            apiResult = await approveSubscription();
        } else {
            apiResult = await declineSubscription();
        }

        if (apiResult.success) {
            // API call successful
            setApiResponse(apiResult.data);

            handleClose();

            if (selectedOption === 'approve') {
                setShowFireworks(true);
                setShowSuccess(true);
                if (onSuccess) onSuccess(selectedOption, apiResult.data);
            } else {
                setShowCancellation(true);
                if (onCancel) onCancel(selectedOption, apiResult.data);
            }
        } else {
            // API call failed
            setShowError(true);
            console.error('Subscription API error:', apiResult.error);
        }
    };

    const replayFireworks = () => {
        setShowFireworks(false);
        setTimeout(() => {
            setShowFireworks(true);
        }, 50);
    };

    const closeSuccess = () => {
        setShowSuccess(false);
        setShowFireworks(false);
    };

    const closeCancellation = () => {
        setShowCancellation(false);
    };

    const handleCloseError = () => {
        setShowError(false);
        resetError();
    };

    const getSubmitButtonProps = () => {
        if (loading) {
            return {
                disabled: true,
                variant: 'contained',
                color: 'inherit',
                children: (
                    <>
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                        Processing...
                    </>
                )
            };
        }

        if (!selectedOption) {
            return { disabled: true, variant: 'contained', color: 'inherit', children: 'Select an Option' };
        }

        if (selectedOption === 'approve') {
            if (!termsAccepted) {
                return { disabled: true, variant: 'contained', color: 'inherit', children: 'Accept Terms to Continue' };
            }
            return { disabled: false, variant: 'contained', color: 'success', children: 'Confirm Approval' };
        }

        return { disabled: false, variant: 'contained', color: 'error', children: 'Confirm Cancellation' };
    };

    const submitButtonProps = getSubmitButtonProps();

    // Adjust padding and font sizes based on screen height
    const getResponsiveStyles = () => {
        if (isSmallScreen) {
            return {
                padding: { xs: 1.5, sm: 2 },
                titleFontSize: { xs: '16px', sm: '18px' },
                bodyFontSize: '0.8rem',
                buttonPadding: '12px',
                optionPadding: 1
            };
        }
        return {
            padding: { xs: 2, sm: 3 },
            titleFontSize: { xs: '18px', sm: '20px' },
            bodyFontSize: '.96rem',
            buttonPadding: '16px',
            optionPadding: 1.5
        };
    };

    const responsiveStyles = getResponsiveStyles();

    // Show loading while checking status
    if (checkingStatus) {
        return (
            <Box
                sx={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 9999,
                    bgcolor: 'rgba(0, 0, 0, 0.7)',
                }}
            >
                <CircularProgress size={60} sx={{ color: '#269e46' }} />
            </Box>
        );
    }

    // Don't render anything if preauth status is not pending
    if (preauthStatus !== 'pending') {
        return null;
    }

    // Don't render anything if not open and not showing success/cancellation
    if (!open && !showSuccess && !showCancellation) return null;

    return (
        <>
            <Modal
                open={open}
                // onClose={handleClose}
                aria-labelledby="preauth-popup-title"
                disableEscapeKeyDown
                sx={{
                    backdropFilter: 'blur(5px)',
                    '& .MuiBackdrop-root': {
                        backgroundColor: 'rgba(0, 0, 0, 0.85)',
                    },
                }}
            >
                <Fade in={open}>
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: { xs: '95%', sm: '90%', md: 500, lg: 650 },
                            maxHeight: '85vh',
                            overflow: 'auto',
                            bgcolor: '#151515',
                            border: '1px solid rgba(38, 158, 70, 0.4)',
                            borderRadius: '12px',
                            background: 'linear-gradient(180deg, rgba(38,158,70,0.12) 0%, rgba(38,158,70,0.00) 28%), #151515',
                            animation: `${pulseGlow} 2s ease-in-out infinite`,
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                height: '2px',
                                bgcolor: '#269e46',
                                boxShadow: '0 0 12px rgba(38, 158, 70, 0.25)',
                            },
                        }}
                    >
                        <Box sx={{ p: responsiveStyles.padding }}>
                            {/* Logo */}
                            {  <Logo /> ? (
                               <Box align="center" sx={{mb:2}}>
                               <Logo />
                              </Box>
                            ) : (
                                <Typography
                                    variant="h4"
                                    align="center"
                                    sx={{
                                        mb: 1.5,
                                        color: '#269e46',
                                        fontWeight: 'bold',
                                        letterSpacing: 1,
                                        fontSize: { xs: '1.5rem', sm: '1.75rem' },
                                    }}
                                >
                                    1HOUSE
                                </Typography>
                            )}

                            <ProgressSteps currentStep={currentStep} />

                            {/* Greeting */}
                            <Typography
                                variant="h5"
                                align="center"
                                sx={{
                                    mb: 1,
                                    color: 'white',
                                    fontWeight: 700,
                                    fontSize: responsiveStyles.titleFontSize,
                                }}
                            >
                                Hello{' '}
                                <Box
                                    component="span"
                                    sx={{
                                        background: 'linear-gradient(90deg, #eeeeee 0%, #eeeeee 40%, #269e46 50%, #eeeeee 60%, #eeeeee 100%)',
                                        backgroundSize: '200% auto',
                                        backgroundClip: 'text',
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                        animation: `${shimmer} 3s linear infinite`,
                                    }}
                                >
                                    {userName?.first_name} {userName?.last_name}
                                </Box>
                            </Typography>

                            {/* Message */}
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="body2" sx={{ color: '#b9b9b9', mb: 1.5, fontSize: responsiveStyles.bodyFontSize, lineHeight: 1.5 }}>
                                    <Box component="strong" sx={{ color: 'white', fontWeight: 700 }}>
                                        Congratulations
                                    </Box>
                                    , you are in the{' '}
                                    <Box component="span" sx={{ color: '#269e46', fontWeight: 700 }}>
                                        1House FIRST 5,000
                                    </Box>
                                    . To keep your 1House subscription active, protected from losing your account, and your{' '}
                                    <Box component="span" sx={{ color: '#269e46', fontWeight: 700 }}>
                                        FIRST 5,000 Status
                                    </Box>
                                    , we need your approval to pre-authorize the credit or debit card you have on file.
                                </Typography>

                                <Typography variant="body2" sx={{ color: '#b9b9b9', mb: 1.5, fontSize: responsiveStyles.bodyFontSize, lineHeight: 1.5 }}>
                                    On{' '}
                                    <Box component="strong" sx={{ color: 'white', fontWeight: 700 }}>
                                        {deadlineDate}
                                    </Box>
                                    , your card will be pre-authorized for the remaining balance due on your 1House subscription.
                                    Once you approve, the actual charge will occur immediately upon successful authorization.
                                </Typography>

                                <Typography variant="body2" sx={{ color: 'white', fontWeight: 500, mb: 1.5, fontSize: responsiveStyles.bodyFontSize }}>
                                    Please choose one of the options below to continue.
                                </Typography>
                            </Box>

                            {/* Approve Option */}
                            <Paper
                                elevation={0}
                                onClick={() => handleOptionSelect('approve')}
                                sx={{
                                    p: responsiveStyles.optionPadding,
                                    mb: 1.5,
                                    borderRadius: '10px',
                                    cursor: 'pointer',
                                    border: '1px solid #333',
                                    bgcolor: 'rgba(255,255,255,0.02)',
                                    transition: 'all 0.2s',
                                    borderColor: selectedOption === 'approve' ? '#269e46' : '#333',
                                    bgcolor: selectedOption === 'approve' ? 'rgba(38, 158, 70, 0.15)' : 'rgba(255,255,255,0.02)',
                                    '&:hover': {
                                        borderColor: 'rgba(38, 158, 70, 0.5)',
                                    },
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                    <Radio
                                        size="small"
                                        checked={selectedOption === 'approve'}
                                        disabled={loading}
                                        sx={{
                                            color: '#555',
                                            '&.Mui-checked': {
                                                color: '#269e46',
                                            },
                                            mt: 0.5,
                                        }}
                                    />
                                    <Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                                            <ShieldOutlined sx={{ color: '#269e46', fontSize: '1rem' }} />
                                            <Typography variant="subtitle2" sx={{ color: 'white', fontWeight: 600, fontSize: '0.96rem' }}>
                                                Approve Subscription
                                            </Typography>
                                        </Box>
                                        <Typography variant="body2" sx={{ color: '#b9b9b9', fontSize: '0.86rem', lineHeight: 1.4 }}>
                                            I authorize 1House to pre-authorize and charge my card on file on {deadlineDate} for the balance due
                                            on my subscription. I understand after approving I can update my card on file by going
                                            to the Billing and updating my default card and adding a backup card, if needed.
                                        </Typography>
                                    </Box>
                                </Box>
                            </Paper>

                            {/* Decline Option */}
                            <Paper
                                elevation={0}
                                onClick={() => !loading && handleOptionSelect('decline')}
                                sx={{
                                    p: responsiveStyles.optionPadding,
                                    mb: 2,
                                    borderRadius: '10px',
                                    cursor: loading ? 'not-allowed' : 'pointer',
                                    border: '1px solid #333',
                                    bgcolor: 'rgba(255,255,255,0.02)',
                                    transition: 'all 0.2s',
                                    borderColor: selectedOption === 'decline' ? '#ef4444' : '#333',
                                    bgcolor: selectedOption === 'decline' ? 'rgba(239, 68, 68, 0.15)' : 'rgba(255,255,255,0.02)',
                                    opacity: loading ? 0.6 : 1,
                                    '&:hover': {
                                        borderColor: loading ? '#333' : 'rgba(239, 68, 68, 0.5)',
                                    },
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                    <Radio
                                        size="small"
                                        checked={selectedOption === 'decline'}
                                        disabled={loading}
                                        sx={{
                                            color: '#555',
                                            '&.Mui-checked': {
                                                color: '#ef4444',
                                            },
                                            mt: 0.5,
                                        }}
                                    />
                                    <Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                                            <WarningOutlined sx={{ color: '#ef4444', fontSize: '1rem' }} />
                                            <Typography variant="subtitle2" sx={{ color: 'white', fontWeight: 600, fontSize: '0.9rem' }}>
                                                Decline Subscription
                                            </Typography>
                                        </Box>
                                        <Typography variant="body2" sx={{ color: '#b9b9b9', fontSize: '0.86rem', lineHeight: 1.4 }}>
                                            I understand that declining will stop my subscription and I will lose access to the
                                            1House services, and my{' '}
                                            <Box component="span" sx={{ color: '#269e46', fontWeight: 700 }}>
                                                FIRST 5,000 Status
                                            </Box>
                                            .
                                        </Typography>
                                    </Box>
                                </Box>
                            </Paper>

                            {/* Terms Box */}
                            <Collapse in={selectedOption === 'approve'}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: responsiveStyles.optionPadding,
                                        mb: 2,
                                        borderRadius: '10px',
                                        border: '1px solid #333',
                                        bgcolor: 'rgba(255,255,255,0.02)',
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                        <Checkbox
                                            size="small"
                                            checked={termsAccepted}
                                            onChange={(e) => setTermsAccepted(e.target.checked)}
                                            disabled={loading}
                                            sx={{
                                                color: '#555',
                                                '&.Mui-checked': {
                                                    color: '#269e46',
                                                },
                                                mt: 0.25,
                                            }}
                                        />
                                        <Typography variant="body2" sx={{ color: '#b9b9b9', fontSize: '0.86rem', lineHeight: 1.4 }}>
                                            By approving, you confirm that the payment method on file is valid and that you agree
                                            to the 1House{' '}
                                            <Button
                                                variant="text"
                                                size="small"
                                                onClick={() => setShowTermsModal(true)}
                                                sx={{
                                                    color: '#269e46',
                                                    textDecoration: 'underline',
                                                    minWidth: 'auto',
                                                    p: 0,
                                                    fontSize: '0.75rem',
                                                    fontWeight: 'inherit',
                                                    '&:hover': {
                                                        bgcolor: 'transparent',
                                                        color: '#2eb852',
                                                    },
                                                }}
                                            >
                                                Terms of Service
                                            </Button>{' '}
                                            and{' '}
                                            <Button
                                                variant="text"
                                                size="small"
                                                onClick={() => setShowBillingModal(true)}
                                                sx={{
                                                    color: '#269e46',
                                                    textDecoration: 'underline',
                                                    minWidth: 'auto',
                                                    p: 0,
                                                    fontSize: '0.75rem',
                                                    fontWeight: 'inherit',
                                                    '&:hover': {
                                                        bgcolor: 'transparent',
                                                        color: '#2eb852',
                                                    },
                                                }}
                                            >
                                                Billing Policy
                                            </Button>
                                            . You may decline at any time prior to {deadlineDate} to avoid being charged.
                                        </Typography>
                                    </Box>
                                </Paper>
                            </Collapse>

                            {/* Submit Button */}
                            <Button
                                fullWidth
                                size="medium"
                                {...submitButtonProps}
                                onClick={handleSubmit}
                                sx={{
                                    py: responsiveStyles.buttonPadding === '16px' ? 1.5 : 1,
                                    borderRadius: '10px',
                                    fontSize: responsiveStyles.buttonPadding === '16px' ? '16px' : '14px',
                                    fontWeight: 700,
                                    textTransform: 'none',
                                    color:"white",
                                    backgroundColor:"primary.main",
                                    '&.Mui-disabled': {
                                        bgcolor: '#333',
                                        color: '#666',
                                    },
                                }}
                            />

                            <Typography
                                variant="caption"
                                align="center"
                                sx={{
                                    display: 'block',
                                    mt: 1.5,
                                    color: '#666',
                                    fontSize: '0.86rem',
                                }}
                            >
                                This pop-up cannot be closed until you select an option above.
                            </Typography>
                        </Box>
                    </Box>
                </Fade>
            </Modal>
 <FireworksEffect active={showFireworks} />

            {/* Success Overlay */}
          <Modal
    open={showSuccess}
    onClose={closeSuccess}
    sx={{
        backdropFilter: 'blur(8px)',
        '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
        },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
    }}
>
    <Box
        sx={{
            width: { xs: '95%', sm: '90%', md: 450 },
            maxWidth: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            textAlign: 'center',
            animation: `${successFadeIn} 0.5s ease-out forwards`,
            bgcolor: '#151515',
            border: '1px solid rgba(38, 158, 70, 0.4)',
            borderRadius: '14px',
            background: 'linear-gradient(180deg, rgba(38,158,70,0.12) 0%, rgba(38,158,70,0.00) 28%), #151515',
            boxShadow: '0 20px 60px rgba(0,0,0,0.8)',
            mx: 'auto',
            display: 'flex',
            flexDirection: 'column',
            '&::-webkit-scrollbar': {
                display: 'none',
            },
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
        }}
    >
        <Box sx={{ 
            p: { xs: 2, sm: 3, md: 4 },
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 1,
        }}>
            {<Logo /> ? (
                <Box align="center" sx={{ mb: 2 }}>
                    <Logo />
                </Box>
            ) : (
                <Typography
                    variant="h4"
                    align="center"
                    sx={{
                        mb: 2,
                        color: '#269e46',
                        fontWeight: 'bold',
                        letterSpacing: 1,
                        fontSize: { xs: '1.5rem', sm: '2rem' },
                    }}
                >
                    1HOUSE
                </Typography>
            )}

            <Typography
                variant="h2"
                sx={{
                    mb: 1.5,
                    color: 'white',
                    fontWeight: 700,
                    fontSize: { xs: '1.25rem', sm: '1.75rem', md: '2rem' },
                    lineHeight: 1.3,
                }}
            >
                🎉 Congratulations! 🎉
            </Typography>

            <Typography
                variant="h1"
                sx={{
                    mb: 2,
                    color: '#269e46',
                    fontWeight: 900,
                    fontSize: { xs: '1.5rem', sm: '2rem', md: '2.25rem' },
                    animation: `${glowPulse} 2s ease-in-out infinite`,
                    lineHeight: 1.2,
                }}
            >
                1House FIRST 5,000
            </Typography>

            <Typography
                variant="body1"
                sx={{
                    mb: 3,
                    color: '#b9b9b9',
                    lineHeight: 1.5,
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    textAlign: 'center',
                }}
            >
                Your subscription has been approved!
                <br />
                You're officially part of the exclusive{' '}
                <Box component="span" sx={{ color: '#269e46', fontWeight: 'bold' }}>
                    FIRST 5,000
                </Box>{' '}
                subscribers.
            </Typography>

            <Box sx={{ my: 2, display: 'flex', justifyContent: 'center' }}>
                <Iconify 
                    icon="material-symbols:check-circle-rounded" 
                    width="80px" 
                    height="80px" 
                    sx={{
                        color: "#229340",
                        width: { xs: '60px', sm: '80px' },
                        height: { xs: '60px', sm: '80px' }
                    }}
                />
            </Box>

            <Typography
                variant="body2"
                sx={{
                    mb: 2,
                    color: '#888',
                    fontSize: { xs: '0.75rem', sm: '0.8rem' },
                }}
            >
                You may now safely close this window.
            </Typography>

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: { xs: 1.5, sm: 1 },
                    justifyContent: 'center',
                    mt: 'auto',
                }}
            >
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<CelebrationOutlined />}
                    onClick={replayFireworks}
                    sx={{
                        borderRadius: '10px',
                        py: 1,
                        px: { xs: 2, sm: 3 },
                        fontSize: { xs: '0.875rem', sm: '0.9rem', md: '1rem' },
                        fontWeight: 600,
                        borderColor: 'rgba(255, 255, 255, 0.2)',
                        bgcolor: 'rgba(255, 255, 255, 0.1)',
                        color: 'white',
                        width: { xs: '100%', sm: 'auto' },
                        minWidth: { sm: '180px' },
                        '&:hover': {
                            bgcolor: 'rgba(255, 255, 255, 0.15)',
                            borderColor: 'rgba(255, 255, 255, 0.3)',
                        },
                    }}
                >
                    Replay Fireworks
                </Button>
                <Button
                    variant="contained"
                    size="small"
                    onClick={closeSuccess}
                    sx={{
                        borderRadius: '10px',
                        py: 1,
                        px: { xs: 2, sm: 3 },
                        fontSize: { xs: '0.875rem', sm: '0.9rem', md: '1rem' },
                        fontWeight: 600,
                        bgcolor: 'rgba(38, 158, 70, 0.18)',
                        border: '1px solid rgba(38, 158, 70, 0.45)',
                        color: 'white',
                        width: { xs: '100%', sm: 'auto' },
                        minWidth: { sm: '140px' },
                        '&:hover': {
                            bgcolor: 'rgba(38, 158, 70, 0.25)',
                        },
                    }}
                >
                    Close Window
                </Button>
            </Box>
        </Box>
    </Box>
</Modal>

            {/* Cancellation Overlay - Updated to "Decline" */}
         
         
         <Modal
    open={showCancellation}
    onClose={closeCancellation}
    sx={{
        backdropFilter: 'blur(8px)',
        '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
        },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
    }}
>
    <Box
        sx={{
            width: { xs: '95%', sm: '90%', md: 450 },
            maxWidth: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            textAlign: 'center',
            animation: `${successFadeIn} 0.5s ease-out forwards`,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: { xs: 2, sm: 3, md: 4 },
            mx: 'auto',
            display: 'flex',
            flexDirection: 'column',
        }}
    >
        {<Logo /> ? (
            <Box align="center" sx={{ mb: 2 }}>
                <Logo />
            </Box>
        ) : (
            <Typography
                variant="h4"
                align="center"
                sx={{
                    mb: 2,
                    color: '#269e46',
                    fontWeight: 'bold',
                    letterSpacing: 1,
                }}
            >
                1HOUSE
            </Typography>
        )}

        <Typography
            variant="h3"
            sx={{
                mb: 1.5,
                color: 'white',
                fontWeight: 700,
                fontSize: { xs: '20px', sm: '24px', md: '27px' },
                lineHeight: 1.3,
            }}
        >
            We're Sorry to See You Go
        </Typography>

        <Box sx={{ my: 2 }}>
            <Iconify 
                icon="wordpress:error" 
                width="80px" 
                height="80px" 
                sx={{ 
                    color: "#ef4444", 
                    fontSize: "2rem",
                    width: { xs: '60px', sm: '80px' },
                    height: { xs: '60px', sm: '80px' }
                }}
            />
        </Box>

        <Typography
            variant="body1"
            sx={{
                mb: 2,
                color: '#b9b9b9',
                lineHeight: 1.5,
                fontSize: { xs: '0.875rem', sm: '1rem' },
                textAlign: 'center',
            }}
        >
            Your subscription has been cancelled. We understand that circumstances change.
            <br />
            <br />
            You can easily <span style={{ color: "white" }}>re-subscribe at any time</span> by visiting your <span style={{ color: "green" }}>Billing</span> page and paying any outstanding balance.
        </Typography>

        <Box sx={{ 
            display: "flex", 
            mb: 3, 
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.5,
        }}>
            <Typography
                variant="body1"
                sx={{
                    color: 'white',
                    fontSize: { xs: '0.875rem', sm: '0.8rem' },
                }}
            >
                For additional questions, email
            </Typography>
            <Typography
                component="a"
                href="mailto:support@1houseglobal.com"
                sx={{
                    color: '#269e46',
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    textDecoration: 'none',
                    '&:hover': {
                        textDecoration: 'underline',
                    },
                }}
            >
                support@1houseglobal.com
            </Typography>
        </Box>

        <Button
            variant="contained"
            size="small"
            onClick={closeCancellation}
            sx={{
                borderRadius: '10px',
                py: 1,
                px: 3,
                fontSize: { xs: '0.875rem', sm: '14px' },
                fontWeight: 600,
                bgcolor: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                color: 'white',
                mt: 'auto',
                '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.15)',
                },
            }}
        >
            Close Window
        </Button>
    </Box>
</Modal>



{/* Terms and Conditions Modal */}
<Modal
  open={showTermsModal}
  onClose={() => setShowTermsModal(false)}
  sx={{
    backdropFilter: 'blur(5px)',
    '& .MuiBackdrop-root': {
      backgroundColor: 'rgba(0, 0, 0, 0.85)',
    },
  }}
>
  <Fade in={showTermsModal}>
    <Box
      sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: { xs: '95%', sm: '90%', md: 600, lg: 700 },
        maxHeight: '85vh',
        overflow: 'auto',
        bgcolor: '#151515',
        border: '1px solid rgba(38, 158, 70, 0.4)',
        borderRadius: '12px',
        background: 'linear-gradient(180deg, rgba(38,158,70,0.12) 0%, rgba(38,158,70,0.00) 28%), #151515',
        '&::-webkit-scrollbar': {
          display: 'none',
        },
        msOverflowStyle: 'none',
        scrollbarWidth: 'none',
      }}
    >
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" sx={{ color: 'white', fontWeight: 700 }}>
            Terms and Conditions
          </Typography>
          <IconButton onClick={() => setShowTermsModal(false)} sx={{ color: '#666' }}>
            <CloseIcon />
          </IconButton>
        </Box>
        
        <Paper
          elevation={0}
          sx={{
            p: 3,
            bgcolor: 'rgba(255,255,255,0.02)',
            border: '1px solid #333',
            borderRadius: '10px',
            maxHeight: '65vh',
            overflow: 'auto',
          }}
        >
          <Typography variant="h6" sx={{ color: '#b9b9b9', mb: 2, fontSize: '0.9rem', lineHeight: 1.6 }}>
            <strong>Effective Date:</strong> September 1, 2025
          </Typography>
          
          <Typography variant="h6" sx={{ color: '#b9b9b9', mb: 2, fontSize: '0.9rem', lineHeight: 1.6 }}>
            <strong>Company:</strong> 1House Global ("Company," "we," "our," or "us")
          </Typography>
          
          <Typography variant="body1" sx={{ color: '#b9b9b9', mb: 3, fontSize: '0.9rem', lineHeight: 1.6 }}>
            These Terms and Conditions ("Terms") govern your access to and use of services provided by 1House Global, including our website(s), booking platforms, communications, and related services (collectively, the "Services"). By using our Services, you agree to these Terms. If you do not agree, you should not use our Services.
          </Typography>
          
          <Typography variant="h6" sx={{ color: 'white', mb: 1.5, fontSize: '1rem', fontWeight: 600 }}>
            1. Use of Services
          </Typography>
          <Typography variant="body1" sx={{ color: '#b9b9b9', mb: 3, fontSize: '0.9rem', lineHeight: 1.6 }}>
            You must be at least 18 years old to use our Services. You agree to provide accurate and complete information when booking appointments, making payments, or otherwise interacting with us. You agree not to use our Services for any unlawful or unauthorized purpose.
          </Typography>
          
          <Typography variant="h6" sx={{ color: 'white', mb: 1.5, fontSize: '1rem', fontWeight: 600 }}>
            2. Appointments, Payments, and Cancellations
          </Typography>
          <Typography variant="body1" sx={{ color: '#b9b9b9', mb: 3, fontSize: '0.9rem', lineHeight: 1.6 }}>
            Appointment bookings may be subject to availability and confirmation by us. You are responsible for paying any applicable service fees, deposits, or cancellation charges in accordance with our posted policies. Cancellations or rescheduling must be done according to the terms provided at the time of booking.
          </Typography>
          
          <Typography variant="h6" sx={{ color: 'white', mb: 1.5, fontSize: '1rem', fontWeight: 600 }}>
            3. SMS Messaging (Appointment Reminders & Confirmations)
          </Typography>
          <Typography variant="body1" sx={{ color: '#b9b9b9', mb: 1, fontSize: '0.9rem', lineHeight: 1.6 }}>
            As part of our Services, we may send appointment reminder and confirmation texts:
          </Typography>
          <Typography variant="body1" sx={{ color: '#b9b9b9', mb: 1, fontSize: '0.9rem', lineHeight: 1.6 }}>
            You can cancel the SMS service at any time. Simply text "STOP" to the shortcode. Upon sending "STOP," we will confirm your unsubscribe status via SMS. You will no longer receive SMS messages from us. To rejoin, sign up as you did initially, and we will resume sending SMS messages to you. If you experience issues with the messaging program, reply with the keyword HELP for more assistance, or contact us at support@1houseglobal.com or call (337) 337-2072. Carriers are not liable for delayed or undelivered messages. Message and data rates may apply for messages sent to you from us and to us from you. You will receive appointment reminder and confirmation texts. For questions about your text plan or data plan, contact your wireless provider.
          </Typography>
          <Typography variant="body1" sx={{ color: '#b9b9b9', mb: 3, fontSize: '0.9rem', lineHeight: 1.6 }}>
            For privacy-related inquiries, please refer to our Privacy Policy at: https://1houseglobal.com/privacy-policy.
          </Typography>
          
          <Typography variant="h6" sx={{ color: 'white', mb: 1.5, fontSize: '1rem', fontWeight: 600 }}>
            4. Intellectual Property
          </Typography>
          <Typography variant="body1" sx={{ color: '#b9b9b9', mb: 3, fontSize: '0.9rem', lineHeight: 1.6 }}>
            All content provided through our Services, including text, graphics, logos, images, software, and other materials, is the property of 1House Global or its licensors. You may not copy, reproduce, distribute, or create derivative works from our content without prior written consent.
          </Typography>
          
          <Typography variant="h6" sx={{ color: 'white', mb: 1.5, fontSize: '1rem', fontWeight: 600 }}>
            5. Disclaimer of Warranties
          </Typography>
          <Typography variant="body1" sx={{ color: '#b9b9b9', mb: 3, fontSize: '0.9rem', lineHeight: 1.6 }}>
            Our Services are provided "as is" and "as available," without warranties of any kind, either express or implied. We do not guarantee uninterrupted or error-free operation of the Services.
          </Typography>
          
          <Typography variant="h6" sx={{ color: 'white', mb: 1.5, fontSize: '1rem', fontWeight: 600 }}>
            6. Limitation of Liability
          </Typography>
          <Typography variant="body1" sx={{ color: '#b9b9b9', mb: 3, fontSize: '0.9rem', lineHeight: 1.6 }}>
            To the maximum extent permitted by law, 1House Global shall not be liable for any damages, including but not limited to direct, indirect, incidental, or consequential damages, arising out of your use of our Services. Carriers are not liable for delayed or undelivered SMS messages.
          </Typography>
          
          <Typography variant="h6" sx={{ color: 'white', mb: 1.5, fontSize: '1rem', fontWeight: 600 }}>
            7. Indemnification
          </Typography>
          <Typography variant="body1" sx={{ color: '#b9b9b9', mb: 3, fontSize: '0.9rem', lineHeight: 1.6 }}>
            You agree to indemnify and hold harmless 1House Global, its affiliates, employees, contractors, and partners from any claims, damages, liabilities, and expenses resulting from your use of the Services or violation of these Terms.
          </Typography>
          
          <Typography variant="h6" sx={{ color: 'white', mb: 1.5, fontSize: '1rem', fontWeight: 600 }}>
            8. Third-Party Links
          </Typography>
          <Typography variant="body1" sx={{ color: '#b9b9b9', mb: 3, fontSize: '0.9rem', lineHeight: 1.6 }}>
            Our website or Services may include links to third-party websites. We are not responsible for the content, practices, or policies of those third-party sites.
          </Typography>
          
          <Typography variant="h6" sx={{ color: 'white', mb: 1.5, fontSize: '1rem', fontWeight: 600 }}>
            9. Governing Law
          </Typography>
          <Typography variant="body1" sx={{ color: '#b9b9b9', mb: 3, fontSize: '0.9rem', lineHeight: 1.6 }}>
            These Terms shall be governed by and construed in accordance with the laws of the State of Louisiana, without regard to its conflict of law principles.
          </Typography>
          
          <Typography variant="h6" sx={{ color: 'white', mb: 1.5, fontSize: '1rem', fontWeight: 600 }}>
            10. Changes to Terms
          </Typography>
          <Typography variant="body1" sx={{ color: '#b9b9b9', mb: 3, fontSize: '0.9rem', lineHeight: 1.6 }}>
            We may update these Terms from time to time. The latest version will always be posted on our website with a revised "Effective Date." Your continued use of the Services after changes are posted constitutes your acceptance of the updated Terms.
          </Typography>
          
          <Typography variant="h6" sx={{ color: 'white', mb: 1.5, fontSize: '1rem', fontWeight: 600 }}>
            11. Contact Us
          </Typography>
          <Typography variant="body1" sx={{ color: '#b9b9b9', mb: 1, fontSize: '0.9rem', lineHeight: 1.6 }}>
            For questions or concerns regarding these Terms, please contact us:
          </Typography>
          <Typography variant="body1" sx={{ color: '#b9b9b9', mb: 1, fontSize: '0.9rem', lineHeight: 1.6 }}>
            <strong>1House Global</strong>
          </Typography>
          <Box sx={{display:"flex"}}>
                        <Typography
                            variant="subtitle2"
                            sx={{
                                mb: 0.5,
                                color: 'white',
                                fontWeight: 600,
                                fontSize: '0.8rem',
                            }}
                        >
                            Email:
                        </Typography>
                        <Typography
                            component="a"
                            href="mailto:support@1houseglobal.com"
                            sx={{
                                color: '#269e46',
                                fontSize: '0.9rem',
                                textDecoration: 'none',
                                pl:2,
                                '&:hover': {
                                    textDecoration: 'underline',
                                },
                            }}
                        >
                            support@1houseglobal.com
                        </Typography>
</Box>

   <Box sx={{display:"flex"}}>
                        <Typography
                            variant="subtitle2"
                            sx={{
                                mb: 0.5,
                                color: 'white',
                                fontWeight: 600,
                                fontSize: '0.8rem',
                            }}
                        >
                            Phone:
                        </Typography>
                        <Typography
                            component="a"
                            href="3373372072"
                            sx={{
                                color: '#269e46',
                                fontSize: '0.9rem',
                                textDecoration: 'none',
                                pl:2,
                                '&:hover': {
                                    textDecoration: 'underline',
                                },
                            }}
                        >
                            (337) 337-2072
                        </Typography>
</Box>

   <Box sx={{display:"flex"}}>
                        <Typography
                            variant="subtitle2"
                            sx={{
                                mb: 0.5,
                                color: 'white',
                                fontWeight: 600,
                                fontSize: '0.8rem',
                            }}
                        >
                            Website:
                        </Typography>
                        <Typography
                            component="a"
                            href="https://1houseglobal.com"
                            sx={{
                                color: '#269e46',
                                fontSize: '0.9rem',
                                textDecoration: 'none',
                                pl:2,
                                '&:hover': {
                                    textDecoration: 'underline',
                                },
                            }}
                        >
                            https://1houseglobal.com
                        </Typography>
</Box>
          {/* <Typography variant="body1" sx={{ color: '#b9b9b9', mb: 1, fontSize: '0.9rem', lineHeight: 1.6 }}>
            <strong>Email:</strong> support@1houseglobal.com
          </Typography> */}
          {/* <Typography variant="body1" sx={{ color: '#b9b9b9', mb: 1, fontSize: '0.9rem', lineHeight: 1.6 }}>
            <strong>Phone:</strong> (337) 337-2072
          </Typography>
          <Typography variant="body1" sx={{ color: '#b9b9b9', mb: 3, fontSize: '0.9rem', lineHeight: 1.6 }}>
            <strong>Website:</strong> https://1houseglobal.com
          </Typography> */}
        </Paper>
        
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Button
            variant="contained"
            onClick={() => setShowTermsModal(false)}
            sx={{
              bgcolor: 'rgba(38, 158, 70, 0.15)',
              border: '1px solid rgba(38, 158, 70, 0.45)',
              color: 'white',
              borderRadius: '10px',
              px: 4,
              '&:hover': {
                bgcolor: 'rgba(38, 158, 70, 0.25)',
              },
            }}
          >
            Close
          </Button>
        </Box>
      </Box>
    </Box>
  </Fade>
</Modal>

{/* Billing Policy Modal */}
<Modal
  open={showBillingModal}
  onClose={() => setShowBillingModal(false)}
  sx={{
    backdropFilter: 'blur(5px)',
    '& .MuiBackdrop-root': {
      backgroundColor: 'rgba(0, 0, 0, 0.85)',
    },
  }}
>
  <Fade in={showBillingModal}>
    <Box
      sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: { xs: '95%', sm: '90%', md: 600, lg: 700 },
        maxHeight: '85vh',
        overflow: 'auto',
        bgcolor: '#151515',
        border: '1px solid rgba(38, 158, 70, 0.4)',
        borderRadius: '12px',
        background: 'linear-gradient(180deg, rgba(38,158,70,0.12) 0%, rgba(38,158,70,0.00) 28%), #151515',
        '&::-webkit-scrollbar': {
          display: 'none',
        },
        msOverflowStyle: 'none',
        scrollbarWidth: 'none',
      }}
    >
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" sx={{ color: 'white', fontWeight: 700 }}>
            Billing Policy
          </Typography>
          <IconButton onClick={() => setShowBillingModal(false)} sx={{ color: '#666' }}>
            <CloseIcon />
          </IconButton>
        </Box>
        
        <Paper
          elevation={0}
          sx={{
            p: 3,
            bgcolor: 'rgba(255,255,255,0.02)',
            border: '1px solid #333',
            borderRadius: '10px',
            maxHeight: '65vh',
            overflow: 'auto',
          }}
        >
          <Typography variant="h6" sx={{ color: '#b9b9b9', mb: 2, fontSize: '0.9rem', lineHeight: 1.6 }}>
            <strong>Effective Date:</strong> February 1, 2025
          </Typography>
          
          <Typography variant="h6" sx={{ color: '#b9b9b9', mb: 2, fontSize: '0.9rem', lineHeight: 1.6 }}>
            <strong>Company:</strong> 1House Global ("Company," "we," "our," or "us")
          </Typography>
          
          <Typography variant="body1" sx={{ color: '#b9b9b9', mb: 3, fontSize: '0.9rem', lineHeight: 1.6 }}>
            This Billing Policy outlines the payment terms, pre-authorization process, and billing practices for subscribers of 1House Global services. By agreeing to this policy, you consent to the terms described below.
          </Typography>
          
          <Typography variant="h6" sx={{ color: 'white', mb: 1.5, fontSize: '1rem', fontWeight: 600 }}>
            1. Initial Launch Pre-Authorization & Charge
          </Typography>
          <Typography variant="body1" sx={{ color: '#b9b9b9', mb: 3, fontSize: '0.9rem', lineHeight: 1.6 }}>
            For our initial launch, all subscriber credit and debit cards will be pre-authorized on February 13, 2025. For subscribers who have approved their subscription, the actual charge will occur immediately upon successful authorization.
          </Typography>
          <Typography variant="body1" sx={{ color: '#ef4444', mb: 3, fontSize: '0.9rem', lineHeight: 1.6, fontStyle: 'italic' }}>
            <strong>Important:</strong> Once you approve your subscription, your card will be charged immediately on February 13th. Please ensure your payment method is valid and has sufficient funds available.
          </Typography>
          
          <Typography variant="h6" sx={{ color: 'white', mb: 1.5, fontSize: '1rem', fontWeight: 600 }}>
            2. Subscriber Consent Requirement
          </Typography>
          <Typography variant="body1" sx={{ color: '#b9b9b9', mb: 3, fontSize: '0.9rem', lineHeight: 1.6 }}>
            All members must explicitly agree to the pre-authorization process before any hold is placed on their payment method. We will only process pre-authorizations for subscribers who have:
          </Typography>
          <Box component="ul" sx={{ color: '#b9b9b9', mb: 3, pl: 2, fontSize: '0.9rem', lineHeight: 1.6 }}>
            <li>Reviewed and accepted this Billing Policy</li>
            <li>Confirmed their agreement through our consent form</li>
            <li>Verified that their payment method on file is valid and current</li>
          </Box>
          
          <Typography variant="h6" sx={{ color: 'white', mb: 1.5, fontSize: '1rem', fontWeight: 600 }}>
            3. Billing Date
          </Typography>
          <Typography variant="body1" sx={{ color: '#b9b9b9', mb: 3, fontSize: '0.9rem', lineHeight: 1.6 }}>
            Subscriber cards that have been successfully pre-authorized and for which explicit consent has been obtained will be charged on February 13, 2025. We will only bill cards that meet both of the following criteria:
          </Typography>
          <Box component="ul" sx={{ color: '#b9b9b9', mb: 3, pl: 2, fontSize: '0.9rem', lineHeight: 1.6 }}>
            <li>A successful pre-authorization has been obtained</li>
            <li>The subscriber has agreed to have their credit card charged</li>
          </Box>
          
          <Typography variant="h6" sx={{ color: 'white', mb: 1.5, fontSize: '1rem', fontWeight: 600 }}>
            4. Cancellation Before Charge
          </Typography>
          <Typography variant="body1" sx={{ color: '#b9b9b9', mb: 3, fontSize: '0.9rem', lineHeight: 1.6 }}>
            You may cancel your subscription at any time prior to February 13, 2025 to avoid being charged. To cancel, please contact our support team or use the cancellation option in your account settings.
          </Typography>
          
          <Typography variant="h6" sx={{ color: 'white', mb: 1.5, fontSize: '1rem', fontWeight: 600 }}>
            5. Recurring Billing
          </Typography>
          <Typography variant="body1" sx={{ color: '#b9b9b9', mb: 3, fontSize: '0.9rem', lineHeight: 1.6 }}>
            After the initial charge on February 13, 2025, your subscription will renew automatically according to your selected billing cycle (monthly or annually). You will be billed at the then-current rate for your subscription plan.
          </Typography>
          
          <Typography variant="h6" sx={{ color: 'white', mb: 1.5, fontSize: '1rem', fontWeight: 600 }}>
            6. Payment Methods
          </Typography>
          <Typography variant="body1" sx={{ color: '#b9b9b9', mb: 1, fontSize: '0.9rem', lineHeight: 1.6 }}>
            We accept the following payment methods:
          </Typography>
          <Box component="ul" sx={{ color: '#b9b9b9', mb: 3, pl: 2, fontSize: '0.9rem', lineHeight: 1.6 }}>
            <li>Major credit cards (Visa, Mastercard, American Express, Discover)</li>
            <li>Debit cards with credit card network logos</li>
            <li>Apple Pay</li>
            <li>Google Pay</li>
          </Box>
          <Typography variant="body1" sx={{ color: '#b9b9b9', mb: 3, fontSize: '0.9rem', lineHeight: 1.6 }}>
            You are responsible for ensuring your payment information is accurate and up to date. Failed payments may result in service interruption.
          </Typography>
          
          <Typography variant="h6" sx={{ color: 'white', mb: 1.5, fontSize: '1rem', fontWeight: 600 }}>
            7. Refunds
          </Typography>
          <Typography variant="body1" sx={{ color: '#b9b9b9', mb: 3, fontSize: '0.9rem', lineHeight: 1.6 }}>
            Refund requests will be evaluated on a case-by-case basis. To request a refund, please contact our support team within 14 days of being charged. Refunds, if approved, will be credited to the original payment method.
          </Typography>
          
          <Typography variant="h6" sx={{ color: 'white', mb: 1.5, fontSize: '1rem', fontWeight: 600 }}>
            8. Failed Payments
          </Typography>
          <Typography variant="body1" sx={{ color: '#b9b9b9', mb: 3, fontSize: '0.9rem', lineHeight: 1.6 }}>
            If a payment fails, we will attempt to process it again within 3-5 business days. After multiple failed attempts, your account may be suspended until payment is successfully processed.
          </Typography>
          
          <Typography variant="h6" sx={{ color: 'white', mb: 1.5, fontSize: '1rem', fontWeight: 600 }}>
            9. Changes to Billing Policy
          </Typography>
          <Typography variant="body1" sx={{ color: '#b9b9b9', mb: 3, fontSize: '0.9rem', lineHeight: 1.6 }}>
            We reserve the right to modify this Billing Policy at any time. Any changes will be communicated to subscribers via email and posted on our website. Continued use of our services after changes are posted constitutes acceptance of the updated policy.
          </Typography>
          
          <Typography variant="h6" sx={{ color: 'white', mb: 1.5, fontSize: '1rem', fontWeight: 600 }}>
            10. Contact Us
          </Typography>
          <Typography variant="body1" sx={{ color: '#b9b9b9', mb: 1, fontSize: '0.9rem', lineHeight: 1.6 }}>
            For billing questions or concerns, please contact us:
          </Typography>
          <Typography variant="body1" sx={{ color: '#b9b9b9', mb: 1, fontSize: '0.9rem', lineHeight: 1.6 }}>
            <strong>1House Global</strong>
          </Typography>
            <Box sx={{display:"flex"}}>
                        <Typography
                            variant="subtitle2"
                            sx={{
                                mb: 0.5,
                                color: 'white',
                                fontWeight: 600,
                                fontSize: '0.8rem',
                            }}
                        >
                            Email:
                        </Typography>
                        <Typography
                            component="a"
                            href="mailto:support@1houseglobal.com"
                            sx={{
                                color: '#269e46',
                                fontSize: '0.9rem',
                                textDecoration: 'none',
                                pl:2,
                                '&:hover': {
                                    textDecoration: 'underline',
                                },
                            }}
                        >
                            support@1houseglobal.com
                        </Typography>
</Box>

   <Box sx={{display:"flex"}}>
                        <Typography
                            variant="subtitle2"
                            sx={{
                                mb: 0.5,
                                color: 'white',
                                fontWeight: 600,
                                fontSize: '0.8rem',
                            }}
                        >
                            Phone:
                        </Typography>
                        <Typography
                            component="a"
                            href="3373372072"
                            sx={{
                                color: '#269e46',
                                fontSize: '0.9rem',
                                textDecoration: 'none',
                                pl:2,
                                '&:hover': {
                                    textDecoration: 'underline',
                                },
                            }}
                        >
                            (337) 337-2072
                        </Typography>
</Box>

   <Box sx={{display:"flex"}}>
                        <Typography
                            variant="subtitle2"
                            sx={{
                                mb: 0.5,
                                color: 'white',
                                fontWeight: 600,
                                fontSize: '0.8rem',
                            }}
                        >
                            Website:
                        </Typography>
                        <Typography
                            component="a"
                            href="https://1houseglobal.com"
                            sx={{
                                color: '#269e46',
                                fontSize: '0.9rem',
                                textDecoration: 'none',
                                pl:2,
                                '&:hover': {
                                    textDecoration: 'underline',
                                },
                            }}
                        >
                            https://1houseglobal.com
                        </Typography>
</Box>
          {/* <Typography variant="body1" sx={{ color: '#b9b9b9', mb: 1, fontSize: '0.9rem', lineHeight: 1.6 }}>
            <strong>Email:</strong> support@1houseglobal.com
          </Typography>
          <Typography variant="body1" sx={{ color: '#b9b9b9', mb: 1, fontSize: '0.9rem', lineHeight: 1.6 }}>
            <strong>Phone:</strong> (337) 337-2072
          </Typography>
          <Typography variant="body1" sx={{ color: '#b9b9b9', mb: 3, fontSize: '0.9rem', lineHeight: 1.6 }}>
            <strong>Website:</strong> https://1houseglobal.com
          </Typography> */}
        </Paper>
        
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Button
            variant="contained"
            onClick={() => setShowBillingModal(false)}
            sx={{
              bgcolor: 'rgba(38, 158, 70, 0.15)',
              border: '1px solid rgba(38, 158, 70, 0.45)',
              color: 'white',
              borderRadius: '10px',
              px: 4,
              '&:hover': {
                bgcolor: 'rgba(38, 158, 70, 0.25)',
              },
            }}
          >
            Close
          </Button>
        </Box>
      </Box>
    </Box>
  </Fade>
</Modal>
        </>
    );
};

export default LoginSubscriptionPopup;
